import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, MagnifyingGlass, CaretLeft, CaretRight, CaretDown, CaretUp,
  Package, User, CalendarBlank, Warning, ClockCountdown,
} from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock, WorkloadBar, CategoryPie } from '../../../components/module-ui';
import { apiPath } from '../../../config/apiBase';
import { authHeaders } from '../api/ticketsApi';

const TICKETS_API = apiPath('/api/tickets');
const PAGE_SIZE = 10;

const DOMAIN_LABELS = {
  hardware: 'Hardware',
  software: 'Software',
  product: 'App Support',
};

const STATE_COLORS = {
  open: { bg: '#DBEAFE', fg: '#1D4ED8', border: '#BFDBFE' },
  in_progress: { bg: '#E0E7FF', fg: '#3730A3', border: '#C7D2FE' },
  pending: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  completed: { bg: '#D1FAE5', fg: '#047857', border: '#A7F3D0' },
  closed: { bg: '#E5E7EB', fg: '#374151', border: '#D1D5DB' },
  cancelled: { bg: '#F3F4F6', fg: '#6B7280', border: '#E5E7EB' },
  reopen: { bg: '#FCE7F3', fg: '#9D174D', border: '#FBCFE8' },
  breached: { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
  at_risk: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  assignment_breached: { bg: '#FCE7F3', fg: '#9D174D', border: '#FBCFE8' },
  assignment_at_risk: { bg: '#E0E7FF', fg: '#3730A3', border: '#C7D2FE' },
};

const VIEW_CARDS = [
  { id: 'all', label: 'All', hint: 'Every status in this scope' },
  { id: 'open', label: 'Open', hint: 'New tickets not yet in progress' },
  { id: 'in_progress', label: 'In progress', hint: 'Currently being worked' },
  { id: 'pending', label: 'On hold', hint: 'Waiting / pending' },
  { id: 'completed', label: 'Completed', hint: 'Work done, not closed' },
  { id: 'closed', label: 'Closed', hint: 'Fully closed' },
  { id: 'cancelled', label: 'Cancelled', hint: 'Cancelled tickets' },
  { id: 'reopen', label: 'Reopened', hint: 'Closed tickets opened again' },
  { id: 'breached', label: 'SLA breached', hint: 'Past resolution due date' },
  { id: 'at_risk', label: 'SLA at risk', hint: 'Nearing resolution due date' },
];

function countForView(summary, id) {
  const by = summary.by_status || {};
  if (id === 'all') return summary.total ?? Object.values(by).reduce((n, v) => n + (v || 0), 0);
  if (id === 'breached') return summary.breached || 0;
  if (id === 'at_risk') return summary.at_risk || 0;
  return by[id] || 0;
}

function StateBadge({ state }) {
  const c = STATE_COLORS[state] || { bg: '#F3F4F6', fg: '#374151', border: '#E5E7EB' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`, textTransform: 'capitalize',
    }}
    >
      {(state === 'pending' ? 'on hold' : (state || '')).replace(/_/g, ' ')}
    </span>
  );
}

function PipelineTrack({ pins = [] }) {
  return (
    <ol className="sla-pipeline-track" aria-label="Ticket pipeline">
      {pins.map((pin, idx) => (
        <li
          key={`${pin.pin}-${idx}`}
          className={`sla-pipeline-pin${pin.critical ? ' is-critical' : ''}${pin.done ? ' is-done' : ''}`}
        >
          <div className="sla-pipeline-pin-dot" aria-hidden />
          <div className="sla-pipeline-pin-body">
            <div className="sla-pipeline-pin-label">{pin.label}</div>
            <div className="sla-pipeline-pin-what"><strong>What:</strong> {pin.what || '—'}</div>
            <div className="sla-pipeline-pin-who"><strong>Who:</strong> {pin.who || '—'}</div>
            <div className="sla-pipeline-pin-when"><strong>When:</strong> {pin.when_ist || '—'}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Domain / Client 360° — ranked breakdown (dashboard style, larger) + ticket list for selection.
 * mode: 'domain' | 'client'
 */
export default function Breakdown360Page({ mode = 'domain' }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusKey = mode === 'domain'
    ? (searchParams.get('domain') || '')
    : (searchParams.get('client') || '');

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [searchRows, setSearchRows] = useState('');

  const [viewMode, setViewMode] = useState('all');
  const [kpiSummary, setKpiSummary] = useState({
    total: 0, by_status: {}, breached: 0, at_risk: 0,
  });
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [payload, setPayload] = useState(null);
  const [ticketError, setTicketError] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expanded, setExpanded] = useState({});

  const title = mode === 'domain' ? 'Tickets by domain' : 'Tickets by client';
  const paramName = mode === 'domain' ? 'domain' : 'client';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${TICKETS_API}/dashboard/breakdown`, {
      ...authHeaders(),
      params: { open_only: true, limit: mode === 'client' ? 50 : 20 },
    }).then((res) => {
      if (cancelled) return;
      const list = mode === 'domain'
        ? (res.data?.by_domain || [])
        : (res.data?.by_client || []);
      setRows(list);
    }).catch((e) => {
      if (!cancelled) {
        setError(e.response?.data?.detail || 'Could not load breakdown');
        setRows([]);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [mode]);

  const ranked = useMemo(() => {
    const term = searchRows.trim().toLowerCase();
    let list = [...rows].sort((a, b) => (b.count || 0) - (a.count || 0));
    if (term) {
      list = list.filter((r) => {
        const name = String(r.name || r.key || '').toLowerCase();
        return name.includes(term) || String(r.key || '').toLowerCase().includes(term);
      });
    }
    return list;
  }, [rows, searchRows]);

  const focusLabel = useMemo(() => {
    if (!focusKey) return '';
    if (mode === 'domain') return DOMAIN_LABELS[focusKey] || focusKey;
    const hit = rows.find((r) => r.key === focusKey);
    return hit?.name || focusKey;
  }, [focusKey, mode, rows]);

  const selectFocus = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('mode', mode);
    if (key) next.set(paramName, key);
    else next.delete(paramName);
    setSearchParams(next, { replace: true });
    setViewMode('all');
    setPage(1);
  };

  // KPI counts for selected client (or all clients)
  useEffect(() => {
    let cancelled = false;
    const params = {
      status_group: 'all',
      page: 1,
      page_size: 1,
    };
    if (focusKey) params[paramName] = focusKey;
    axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
      ...authHeaders(),
      params,
    }).then((res) => {
      if (cancelled) return;
      const s = res.data?.summary || {};
      setKpiSummary({
        total: s.total || 0,
        by_status: s.by_status || {},
        breached: s.breached || 0,
        at_risk: s.at_risk || 0,
      });
    }).catch(() => {
      if (!cancelled) setKpiSummary({ total: 0, by_status: {}, breached: 0, at_risk: 0 });
    });
    return () => { cancelled = true; };
  }, [focusKey, paramName]);

  const fetchPipeline = useCallback(async () => {
    setLoadingTickets(true);
    setTicketError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
      };
      if (focusKey) params[paramName] = focusKey;
      if (viewMode === 'all') {
        params.status_group = 'all';
      } else if (viewMode === 'breached' || viewMode === 'at_risk') {
        params.status_group = 'open';
        params.pipeline_state = viewMode;
      } else {
        params.status = viewMode;
      }
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
        ...authHeaders(),
        params,
      });
      setPayload(res.data);
      const s = res.data?.summary || {};
      if (s.by_status) {
        setKpiSummary({
          total: s.total || 0,
          by_status: s.by_status || {},
          breached: s.breached || 0,
          at_risk: s.at_risk || 0,
        });
      }
    } catch {
      setTicketError('Could not load tickets');
      setPayload(null);
    } finally {
      setLoadingTickets(false);
    }
  }, [focusKey, paramName, page, q, viewMode]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [focusKey, viewMode]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === q) return;
      setPage(1);
      setQ(next);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, q]);

  const chartData = ranked.map((r) => ({
    key: r.key,
    name: mode === 'domain' ? (DOMAIN_LABELS[r.key] || r.name || r.key) : (r.name || r.key),
    count: r.count,
    value: r.count,
  }));

  const items = payload?.items || [];
  const pagination = payload?.pagination || {
    page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1, has_prev: false, has_next: false,
  };
  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);
  const viewMeta = VIEW_CARDS.find((c) => c.id === viewMode) || VIEW_CARDS[0];

  if (loading) {
    return (
      <ModuleShell title={title} subtitle="Loading…">
        <SkeletonBlock rows={10} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title={title}
      subtitle={
        focusKey
          ? `${focusLabel} — click a card to list those tickets`
          : `Click a status card to list tickets. Click a ${mode} to narrow the list.`
      }
      headerActions={(
        <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Overview
        </button>
      )}
    >
      <div data-testid={`breakdown-360-${mode}`} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{ padding: 12, background: '#FEE2E2', color: '#B91C1C', borderRadius: 8 }}>{error}</div>
        )}

        <div className="sla-pipeline-summary" role="group" aria-label="Ticket counts">
          {VIEW_CARDS.map((chip) => {
            const selected = viewMode === chip.id;
            const value = countForView(kpiSummary, chip.id);
            return (
              <button
                key={chip.id}
                type="button"
                className={`sla-summary-chip${selected ? ' is-active' : ''}`}
                onClick={() => { setViewMode(chip.id); setPage(1); }}
                aria-pressed={selected}
                aria-label={`${chip.label}: ${value} tickets`}
              >
                <span className="sla-summary-chip-value">{value}</span>
                <span className="sla-summary-chip-label">{chip.label}</span>
              </button>
            );
          })}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: mode === 'domain' ? 'minmax(0, 1fr) minmax(0, 1.2fr)' : '1fr',
          gap: 16,
        }}
        className="breakdown-360-grid"
        >
          {mode === 'domain' && (
            <CategoryPie
              data={chartData}
              title="Domain share"
              subtitle="Click a slice to load tickets"
              height={320}
              onSliceClick={(key) => selectFocus(key)}
            />
          )}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
              <form className="sla-api-search" style={{ margin: 0, maxWidth: 280 }} onSubmit={(e) => e.preventDefault()}>
                <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
                <input
                  type="search"
                  value={searchRows}
                  onChange={(e) => setSearchRows(e.target.value)}
                  placeholder={mode === 'domain' ? 'Search domains…' : 'Search clients…'}
                />
              </form>
              {focusKey && (
                <button type="button" className="tk-btn tk-btn-secondary" onClick={() => selectFocus('')}>
                  Clear selection
                </button>
              )}
            </div>
            <WorkloadBar
              data={chartData}
              title={mode === 'domain' ? 'Domains ranked' : 'Clients ranked'}
              subtitle="Open tickets · click for detail"
              valueLabel="open"
              maxItems={40}
              onBarClick={(key) => selectFocus(key)}
            />
          </div>
        </div>

        <section className="sla-pipeline-panel" style={{ margin: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                    {focusLabel || (mode === 'client' ? 'All clients' : 'All domains')} · {viewMeta.label}
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>{viewMeta.hint}</p>
                </div>
                <Link
                  to={`/dashboard/tickets/list?${[
                    focusKey ? `${paramName}=${encodeURIComponent(focusKey)}` : '',
                    (viewMode === 'closed' || viewMode === 'cancelled') ? 'status_group=closed' : '',
                  ].filter(Boolean).join('&')}`}
                  className="tk-btn tk-btn-secondary"
                >
                  Ticket list
                </Link>
              </div>

              <div className="sla-pipeline-filters">
                <form
                  className="sla-api-search"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setPage(1);
                    setQ(searchInput.trim());
                  }}
                >
                  <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search tickets…"
                  />
                  <button type="submit" className="tk-btn tk-btn-secondary">Search</button>
                </form>
              </div>

              {ticketError && <p style={{ color: '#B91C1C', fontSize: 13 }}>{ticketError}</p>}
              {loadingTickets && !payload ? (
                <SkeletonBlock rows={4} />
              ) : items.length === 0 ? (
                <div className="sla-pipeline-empty">
                  <ClockCountdown size={28} color="#9CA3AF" />
                  <p>{q ? `No tickets match “${q}”.` : 'No tickets in this view.'}</p>
                </div>
              ) : (
                <>
                  <div className="sla-pipeline-grid">
                    {items.map((item) => {
                      const open = !!expanded[item.ticket_no];
                      const productName = item.product?.product_name || item.product?.product_key || '—';
                      const assignees = (item.who?.assignee_names || []).join(', ') || 'Unassigned';
                      return (
                        <div key={item.ticket_no} className={`sla-pipeline-card${open ? ' is-expanded' : ''}`}>
                          <div className="sla-pipeline-row-main">
                            <div className="sla-pipeline-row-top">
                              <Link
                                to={`/dashboard/tickets/list/${encodeURIComponent(item.ticket_no)}`}
                                className="sla-ticket-link"
                              >
                                {item.ticket_no}
                              </Link>
                              <StateBadge state={item.what?.status || item.primary_state} />
                              {item.states?.includes('breached') && item.what?.status !== 'breached' && (
                                <StateBadge state="breached" />
                              )}
                            </div>
                            <div className="sla-pipeline-what">
                              <strong>What:</strong> {item.what?.subject || '—'}
                            </div>
                            <div className="sla-pipeline-meta">
                              <span><User size={14} /> {assignees}</span>
                              <span><CalendarBlank size={14} /> {item.when?.due_date_ist || item.when?.created_at_ist || '—'}</span>
                              <span><Package size={14} /> {productName}</span>
                              <span><Warning size={14} /> {item.what?.client || '—'}</span>
                            </div>
                          </div>
                          <div className="sla-pipeline-row-actions">
                            <button
                              type="button"
                              className="tk-btn tk-btn-secondary"
                              onClick={() => setExpanded((prev) => ({ ...prev, [item.ticket_no]: !prev[item.ticket_no] }))}
                            >
                              {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
                              Stages
                            </button>
                          </div>
                          {open && (
                            <div className="sla-pipeline-detail">
                              <PipelineTrack pins={item.pipeline || []} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="sla-pagination">
                    <span className="sla-pagination-meta">Showing {from}–{to} of {pagination.total}</span>
                    <div className="sla-pagination-buttons">
                      <button type="button" className="tk-btn tk-btn-secondary" disabled={!pagination.has_prev || loadingTickets} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        <CaretLeft size={16} /> Previous
                      </button>
                      <span className="sla-pagination-page">Page {pagination.page} / {pagination.total_pages}</span>
                      <button type="button" className="tk-btn tk-btn-secondary" disabled={!pagination.has_next || loadingTickets} onClick={() => setPage((p) => p + 1)}>
                        Next <CaretRight size={16} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .breakdown-360-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </ModuleShell>
  );
}
